import { REQUEST_TIMEOUT_MS } from 'constants/requestLimits'
import { reportRequestFailure } from '../requestFailureReporter'

const isServerFailure = (status: number): boolean => status >= 500 || status === 429

/**
 * `fetch` with an abort-based timeout so a high-latency request fails fast
 * (as a retryable AbortError) instead of hanging indefinitely. Reports every
 * server-side failure, timeout and network error for stability monitoring —
 * client errors (4xx) are left to the caller. See PX-3333.
 *
 * Returns the raw {@link Response}; callers remain responsible for their own
 * success/error handling.
 */
const fetchWithTimeout = async (
  url: string,
  init: RequestInit = {},
  timeoutMs: number = REQUEST_TIMEOUT_MS
): Promise<Response> => {
  const controller = new AbortController()

  let timedOut = false
  const timeoutId = setTimeout(() => {
    timedOut = true
    controller.abort()
  }, timeoutMs)

  // Forward a caller-provided signal so their cancellation still aborts the
  // fetch (our own signal would otherwise shadow it).
  const callerSignal = init.signal ?? undefined
  const forwardAbort = () => controller.abort()

  if (callerSignal?.aborted) controller.abort()
  else callerSignal?.addEventListener('abort', forwardAbort)

  let response: Response

  try {
    response = await fetch(url, { ...init, signal: controller.signal })
  } catch (error) {
    const isAbort = error instanceof Error && error.name === 'AbortError'

    // A caller-initiated cancellation is not a service failure — only report
    // aborts we triggered via the timeout.
    if (!isAbort || timedOut) {
      reportRequestFailure({ kind: isAbort ? 'timeout' : 'network', url })
    }
    throw error
  } finally {
    clearTimeout(timeoutId)
    callerSignal?.removeEventListener('abort', forwardAbort)
  }

  if (isServerFailure(response.status)) {
    reportRequestFailure({ kind: 'server', status: response.status, url })
  }

  return response
}

export { fetchWithTimeout }
